import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Receipt,
  RefreshCw,
  RotateCcw,
  Search,
  Ticket,
  TrendingUp,
} from "lucide-react";
import {
  BuyOrderFinancial,
  BuyOrderFinancialsSummary,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./OrderFinancials.module.css";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const RANGE_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Today", value: "today" },
  { label: "Last Week", value: "last_week" },
  { label: "1 Month", value: "month" },
] as const;

type RangeFilter = typeof RANGE_OPTIONS[number]["value"];

const emptySummary: BuyOrderFinancialsSummary = {
  order_count: 0,
  ticket_quantity: 0,
  customer_subtotal: 0,
  customer_tax: 0,
  customer_total: 0,
  refunded_amount: 0,
  net_customer_collected: 0,
  te_ticket_cost: 0,
  te_tax_total: 0,
  te_service_fee_total: 0,
  qa_pay_to_te_total: 0,
  stripe_processing_fee_total: 0,
  gross_spread: 0,
  net_after_stripe: 0,
  refund_adjusted_net_after_stripe: 0,
  gap_order_count: 0,
};

const formatCurrency = (value: number | null | undefined) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value: string | null | undefined) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const toTitleCase = (value: string | null | undefined) => {
  if (!value) return "-";
  return value
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getCustomer = (row: BuyOrderFinancial) =>
  row.user_name || row.user_email || row.user_phone || row.user_id || "-";

const getTileCustomerLabel = (status: string) =>
  status.toLowerCase() === "refunded" ? "Net Customer Collected" : "Customer Charged";

const getTileCustomerValue = (summary: BuyOrderFinancialsSummary, status: string) =>
  status.toLowerCase() === "refunded" ? summary.net_customer_collected : summary.customer_total;

const getTileNetValue = (summary: BuyOrderFinancialsSummary, status: string) =>
  status.toLowerCase() === "refunded" ? summary.refund_adjusted_net_after_stripe : summary.net_after_stripe;

const getRangeBounds = (range: RangeFilter) => {
  if (range === "all") return {};

  const now = new Date();
  const from = new Date(now);

  if (range === "today") {
    from.setHours(0, 0, 0, 0);
  } else if (range === "last_week") {
    from.setDate(now.getDate() - 7);
  } else if (range === "month") {
    from.setMonth(now.getMonth() - 1);
  }

  return {
    from: from.toISOString(),
    to: now.toISOString(),
  };
};

const BuyAdminOrderFinancials = () => {
  const [rows, setRows] = useState<BuyOrderFinancial[]>([]);
  const [summary, setSummary] = useState<BuyOrderFinancialsSummary>(emptySummary);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("");
  const [customer, setCustomer] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [completedOnly, setCompletedOnly] = useState(false);
  const [rangeFilter, setRangeFilter] = useState<RangeFilter>("all");
  const [orderStatusOptions, setOrderStatusOptions] = useState<string[]>([]);
  const [paymentStatusOptions, setPaymentStatusOptions] = useState<string[]>([]);
  const [searchVersion, setSearchVersion] = useState(0);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const offset = page * pageSize;

  const loadOrderFinancials = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await buyAdminApi.getOrderFinancials({
        limit: pageSize,
        offset,
        order: order.trim(),
        customer: customer.trim(),
        orderStatus,
        paymentStatus,
        completedOnly: completedOnly ? "true" : "false",
        ...getRangeBounds(rangeFilter),
      });

      setRows(response.data);
      setTotal(response.pagination.total);
      setSummary(response.summary || emptySummary);
      setOrderStatusOptions(response.options?.order_statuses?.filter(Boolean) || []);
      setPaymentStatusOptions(response.options?.payment_statuses?.filter(Boolean) || []);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load order financials.");
      setRows([]);
      setTotal(0);
      setSummary(emptySummary);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrderFinancials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, orderStatus, paymentStatus, completedOnly, rangeFilter, searchVersion]);

  const handleSearch = () => {
    setPage(0);
    setSearchVersion((prev) => prev + 1);
  };

  const handleReset = () => {
    setOrder("");
    setCustomer("");
    setOrderStatus("");
    setPaymentStatus("");
    setCompletedOnly(false);
    setRangeFilter("all");
    setPage(0);
    setSearchVersion((prev) => prev + 1);
  };

  return (
    <div className={`${styles.page} buy-admin-theme-scope`}>
      <div className={styles.header}>
        <div>
          <div className={styles.titleRow}>
            <DollarSign size={22} />
            <h1>Orders</h1>
          </div>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <div className={styles.rangeTabs}>
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                className={`${styles.rangeTab} ${rangeFilter === option.value ? styles.rangeTabActive : ""}`}
                onClick={() => { setRangeFilter(option.value); setPage(0); }}
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void loadOrderFinancials()}
            disabled={loading}
            aria-label="Refresh order financials"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : undefined} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      <div className={styles.metrics}>
        <div className={`${styles.metric} ${styles.metricRevenue}`}>
          <div>
            <span>Orders</span>
            <strong>{summary.order_count}</strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconRevenue}`}>
            <Ticket size={18} />
          </div>
        </div>
        <div className={`${styles.metric} ${styles.metricPayout}`}>
          <div>
            <span>{getTileCustomerLabel(paymentStatus)}</span>
            <strong>{formatCurrency(getTileCustomerValue(summary, paymentStatus))}</strong>
            {paymentStatus.toLowerCase() === "refunded" && (
              <small>Refunded {formatCurrency(summary.refunded_amount)}</small>
            )}
          </div>
          <div className={`${styles.metricIcon} ${styles.iconPayout}`}>
            <DollarSign size={18} />
          </div>
        </div>
        <div className={`${styles.metric} ${styles.metricFee}`}>
          <div>
            <span>Pay To TE</span>
            <strong>{formatCurrency(summary.qa_pay_to_te_total)}</strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconFee}`}>
            <Receipt size={18} />
          </div>
        </div>
        <div className={`${styles.metric} ${styles.metricRevenue}`}>
          <div>
            <span>Stripe Fees</span>
            <strong>{formatCurrency(summary.stripe_processing_fee_total)}</strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconRevenue}`}>
            <Receipt size={18} />
          </div>
        </div>
        <div className={`${styles.metric} ${styles.metricProfit}`}>
          <div>
            <span>Net After Stripe</span>
            <strong>{formatCurrency(getTileNetValue(summary, paymentStatus))}</strong>
          </div>
          <div className={`${styles.metricIcon} ${styles.iconProfit}`}>
            <TrendingUp size={18} />
          </div>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.inputGroup}>
          <label>Order</label>
          <input value={order} onChange={(event) => setOrder(event.target.value)} placeholder="QA-MPW..." />
        </div>
        <div className={styles.inputGroup}>
          <label>Customer</label>
          <input value={customer} onChange={(event) => setCustomer(event.target.value)} placeholder="email, phone, or name" />
        </div>
        <div className={styles.inputGroup}>
          <label>Order Status</label>
          <select value={orderStatus} onChange={(event) => { setOrderStatus(event.target.value); setPage(0); }}>
            <option value="">All Statuses</option>
            {orderStatusOptions.map((status) => (
              <option key={status} value={status}>{toTitleCase(status)}</option>
            ))}
          </select>
        </div>
        <div className={styles.inputGroup}>
          <label>Payment Status</label>
          <select value={paymentStatus} onChange={(event) => { setPaymentStatus(event.target.value); setPage(0); }}>
            <option value="">All</option>
            {paymentStatusOptions.map((status) => (
              <option key={status} value={status}>{toTitleCase(status)}</option>
            ))}
          </select>
        </div>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={completedOnly}
            onChange={(event) => { setCompletedOnly(event.target.checked); setPage(0); }}
          />
          Completed only
        </label>
        <button className={styles.searchButton} onClick={handleSearch} aria-label="Search" title="Search">
          <Search size={16} />
        </button>
        <button className={styles.resetButton} onClick={handleReset} aria-label="Reset" title="Reset">
          <RotateCcw size={16} />
        </button>
      </div>

      {errorMessage && (
        <div className={styles.errorBanner}>
          <AlertCircle size={16} />
          {errorMessage}
        </div>
      )}

      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.emptyState}>Loading orders...</div>
        ) : rows.length === 0 ? (
          <div className={styles.emptyState}>No orders found.</div>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Created</th>
                  <th>Customer</th>
                  <th>Order Status</th>
                  <th>Payment Status</th>
                  <th>Tickets</th>
                  <th>Customer Total</th>
                  <th>Refunded</th>
                  <th>Tax</th>
                  <th>Pay To TE</th>
                  <th>TE Breakup</th>
                  <th>Stripe Fee</th>
                  <th>Net After Stripe</th>
                  <th>TE Order</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.order_id}>
                    <td>
                      <strong>{row.order_number || `#${row.order_id}`}</strong>
                      {row.has_te_financials_gap && <div className={styles.warningText}>TE price fallback</div>}
                    </td>
                    <td>{formatDate(row.created_at)}</td>
                    <td>
                      <div>{getCustomer(row)}</div>
                      {row.user_email && <div className={styles.subText}>{row.user_email}</div>}
                    </td>
                    <td>{toTitleCase(row.order_status)}</td>
                    <td>{toTitleCase(row.payment_status)}</td>
                    <td>{row.ticket_quantity}</td>
                    <td>{formatCurrency(row.customer_total)}</td>
                    <td>{formatCurrency(row.refunded_amount)}</td>
                    <td>{formatCurrency(row.customer_tax)}</td>
                    <td>{formatCurrency(row.qa_pay_to_te_total)}</td>
                    <td className={styles.breakupCell}>
                      Tickets {formatCurrency(row.te_ticket_cost)}
                      <br />
                      Tax {formatCurrency(row.te_tax_total)}
                      <br />
                      Fee {formatCurrency(row.te_service_fee_total)}
                    </td>
                    <td>{formatCurrency(row.stripe_processing_fee_total)}</td>
                    <td className={row.net_after_stripe >= 0 ? styles.positiveAmount : styles.negativeAmount}>
                      {formatCurrency(row.net_after_stripe)}
                    </td>
                    <td>{row.ev_order_id || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.footer}>
          <span>Page {page + 1} of {totalPages}</span>
          <div className={styles.pagination}>
            <button disabled={page === 0 || loading} onClick={() => setPage((prev) => Math.max(0, prev - 1))}>
              <ChevronLeft size={16} />
            </button>
            <button disabled={page + 1 >= totalPages || loading} onClick={() => setPage((prev) => prev + 1)}>
              <ChevronRight size={16} />
            </button>
            <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }}>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size} / page</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyAdminOrderFinancials;
